import React, { useCallback, useMemo, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useEraDownload,
  useGetProviderRemittanceDetailQuery,
} from '../../../store';
import { formatDateOnly } from '../../../utils';
import { formatCents } from '../shared';

interface RemittanceDetailPaneProps {
  remittanceId: string | null;
  open: boolean;
  onClose: () => void;
}

// Claim status labels
const statusLabels: Record<string, string> = {
  '1': 'Processed Primary',
  '2': 'Processed Secondary',
  '3': 'Processed Tertiary',
  '4': 'Denied',
  '19': 'Processed Primary, Forwarded',
  '20': 'Processed Secondary, Forwarded',
  '22': 'Reversal',
};

const statusColors: Record<string, { bg: string; text: string }> = {
  '1': { bg: '#F0FDF4', text: '#166534' },
  '2': { bg: '#F0FDF4', text: '#166534' },
  '4': { bg: '#FEF2F2', text: '#991B1B' },
  '22': { bg: '#FFFBEB', text: '#92400E' },
};

// Adjustment group code labels (PR label is set dynamically in the component)
const getGroupCodeLabels = (singularCap: string): Record<string, string> => ({
  CO: 'Contractual Obligation',
  PR: `${singularCap} Responsibility`,
  OA: 'Other Adjustment',
  PI: 'Payer Initiated',
  CR: 'Corrections/Reversals',
});

function formatDate(iso: string | null | undefined): string {
  return formatDateOnly(iso, { month: 'short', day: 'numeric', year: 'numeric' }, '—');
}

function formatPaymentMethod(code: string | null | undefined): string {
  if (!code) return '—';
  if (code === 'CHK' || code === 'Check') return 'Check';
  if (code === 'ACH') return 'ACH';
  return code;
}

export const RemittanceDetailPane: React.FC<RemittanceDetailPaneProps> = ({
  remittanceId,
  open,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { singularCap } = useSubjectLanguageVariants();
  const groupCodeLabels = useMemo(() => getGroupCodeLabels(singularCap), [singularCap]);
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetProviderRemittanceDetailQuery(
    { remittanceId: remittanceId ?? '' },
    { skip: !remittanceId || !open },
  );

  const { downloadEra, isDownloading: isDownloadingEra } = useEraDownload();

  const handleDownloadEra = useCallback(async () => {
    if (!remittanceId) return;
    try {
      await downloadEra(remittanceId);
    } catch {
      enqueueSnackbar('Failed to download ERA file', { variant: 'error' });
    }
  }, [remittanceId, downloadEra, enqueueSnackbar]);

  const remittance = data?.remittance;
  const claims = data?.claims ?? [];
  const isStedi = remittance?.source_system === 'stedi';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose} slotProps={{ paper: {
        sx: {
          width: { xs: '100%', sm: 520, md: 600 },
          bgcolor: 'grey.50',
        },
      } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Payment Detail
          </Typography>
          {remittance && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {remittance.check_or_eft_trace_number}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isStedi && (
            <Tooltip title="Download ERA (835)">
              <IconButton onClick={handleDownloadEra} disabled={isDownloadingEra} size="small">
                {isDownloadingEra ? <CircularProgress size={18} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">Failed to load remittance details</Alert>
          </Box>
        )}

        {remittance && !isLoading && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Summary card */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
                p: 2.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1.5 }}>
                {remittance.payer_name ?? 'Unknown Payer'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase', fontWeight: 600 }}>
                    Total Paid
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.125rem' }}>
                    {formatCents(remittance.total_payment_amount_cents)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase', fontWeight: 600 }}>
                    Claims
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                    {remittance.claim_count ?? claims.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase', fontWeight: 600 }}>
                    Payment Date
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    {formatDate(remittance.payment_date)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Chip
                  label={formatPaymentMethod(remittance.payment_method_code)}
                  size="small"
                  sx={{ fontSize: '0.625rem', height: 20 }}
                />
                {remittance.application_mode === 'test' && (
                  <Chip label="Test" size="small" color="warning" sx={{ fontSize: '0.625rem', height: 20 }} />
                )}
              </Box>
            </Box>

            {/* Intelligence placeholder */}
            <Box
              sx={{
                bgcolor: '#FDFCFF',
                border: '1px solid #E6E1FF',
                borderRadius: '10px',
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'primary.main' }}>
                  Payment Summary
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {claims.length} claim{claims.length !== 1 ? 's' : ''} processed
                {(() => {
                  const denied = claims.filter(c => c.claim_status_code === '4').length;
                  const reversed = claims.filter(c => c.claim_status_code === '22').length;
                  const parts: string[] = [];
                  if (denied) parts.push(`${denied} denied`);
                  if (reversed) parts.push(`${reversed} reversed`);
                  return parts.length ? ` · ${parts.join(', ')}` : ' · all clean';
                })()}
              </Typography>
            </Box>

            {/* Claims accordion */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                Claims
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {claims.map(claim => {
                  const isExpanded = expandedClaimId === claim.remittance_claim_id;
                  const statusColor = statusColors[claim.claim_status_code] ?? { bg: '#F3F4F6', text: '#6B7280' };
                  const patientName = [claim.patient_first_name, claim.patient_last_name].filter(Boolean).join(' ') || claim.patient_control_number;
                  const hasPatientResp = (claim.patient_responsibility_amount_cents ?? 0) > 0;

                  return (
                    <Box
                      key={claim.remittance_claim_id}
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Claim header */}
                      <Box
                        onClick={() => setExpandedClaimId(isExpanded ? null : claim.remittance_claim_id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(54, 34, 158, 0.02)' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                              {patientName}
                            </Typography>
                            <Chip
                              label={statusLabels[claim.claim_status_code] ?? 'Unknown'}
                              size="small"
                              sx={{
                                bgcolor: statusColor.bg,
                                color: statusColor.text,
                                fontSize: '0.5625rem',
                                height: 18,
                                fontWeight: 500,
                              }}
                            />
                            {claim.forwarded_to_secondary && (
                              <Chip
                                label={
                                  claim.crossover_carrier_name
                                    ? `Fwd to Secondary · ${claim.crossover_carrier_name}`
                                    : 'Fwd to Secondary'
                                }
                                size="small"
                                sx={{ fontSize: '0.5625rem', height: 18 }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                              Billed {formatCents(claim.total_claim_charge_amount_cents)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#16A34A', fontSize: '0.6875rem', fontWeight: 500 }}>
                              Paid {formatCents(claim.claim_payment_amount_cents)}
                            </Typography>
                            {hasPatientResp && (
                              <Typography variant="caption" sx={{ color: '#D97706', fontSize: '0.6875rem', fontWeight: 500 }}>
                                Patient {formatCents(claim.patient_responsibility_amount_cents)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18,
                            color: 'text.secondary',
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>

                      {/* Expanded detail */}
                      <Collapse in={isExpanded}>
                        <Divider />
                        <Box sx={{ px: 2, py: 1.5 }}>
                          {/* Claim metadata */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1.5 }}>
                            {claim.payer_claim_control_number && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase' }}>
                                  Payer Ref
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {claim.payer_claim_control_number}
                                </Typography>
                              </Box>
                            )}
                            {claim.rendering_provider_npi && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase' }}>
                                  Rendering NPI
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {claim.rendering_provider_npi}
                                </Typography>
                              </Box>
                            )}
                            {claim.statement_period_start && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase' }}>
                                  Statement Period
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {formatDate(claim.statement_period_start)} – {formatDate(claim.statement_period_end)}
                                </Typography>
                              </Box>
                            )}
                            {claim.claim_received_date && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase' }}>
                                  Received
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {formatDate(claim.claim_received_date)}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Claim-level adjustments */}
                          {claim.claim_adjustments && (claim.claim_adjustments as Record<string, unknown>[]).length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Claim Adjustments
                              </Typography>
                              {(claim.claim_adjustments as Array<{ groupCode?: string; reasonCode?: string; reasonDescription?: string; amountCents?: number }>).map((adj, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.25, fontSize: '0.6875rem' }}>
                                  <Chip
                                    label={adj.groupCode ?? '?'}
                                    size="small"
                                    sx={{ fontSize: '0.5625rem', height: 16, fontWeight: 600 }}
                                  />
                                  <Typography variant="caption" sx={{ flex: 1, fontSize: '0.6875rem' }}>
                                    {adj.reasonCode} — {adj.reasonDescription ?? 'Unknown'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 500 }}>
                                    {formatCents(adj.amountCents)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Service lines */}
                          {claim.lines && claim.lines.length > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5625rem', textTransform: 'uppercase', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Service Lines
                              </Typography>
                              {claim.lines.map(line => (
                                <Box key={line.remittance_line_id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                                      {line.adjudicated_procedure_code ?? '—'}
                                    </Typography>
                                    {line.adjudicated_procedure_modifier_codes?.map(mod => (
                                      <Chip key={mod} label={mod} size="small" sx={{ fontSize: '0.5rem', height: 16 }} />
                                    ))}
                                    {line.submitted_procedure_code && line.submitted_procedure_code !== line.adjudicated_procedure_code && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem' }}>
                                        (submitted: {line.submitted_procedure_code})
                                      </Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem' }}>
                                      {formatDate(line.service_date)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.25, pl: '68px' }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>
                                      Billed {formatCents(line.line_item_charge_amount_cents)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.625rem', color: '#16A34A', fontWeight: 500 }}>
                                      Paid {formatCents(line.line_item_payment_amount_cents)}
                                    </Typography>
                                  </Box>

                                  {/* Line adjustments */}
                                  {line.service_adjustments && (line.service_adjustments as Array<{ groupCode?: string; reasonCode?: string; reasonDescription?: string; amountCents?: number }>).length > 0 && (
                                    <Box sx={{ pl: '68px', mt: 0.5 }}>
                                      {(line.service_adjustments as Array<{ groupCode?: string; reasonCode?: string; reasonDescription?: string; amountCents?: number }>).map((adj, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 0.75, py: 0.125 }}>
                                          <Tooltip title={groupCodeLabels[adj.groupCode ?? ''] ?? adj.groupCode}>
                                            <Chip label={adj.groupCode} size="small" sx={{ fontSize: '0.5rem', height: 14 }} />
                                          </Tooltip>
                                          <Typography variant="caption" sx={{ fontSize: '0.625rem', color: 'text.secondary', flex: 1 }}>
                                            {adj.reasonCode}: {adj.reasonDescription}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.625rem' }}>
                                            {formatCents(adj.amountCents)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}

                                  {/* Remark codes */}
                                  {line.remark_codes && (line.remark_codes as Array<{ remarkCode?: string; remark?: string }>).length > 0 && (
                                    <Box sx={{ pl: '68px', mt: 0.25, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {(line.remark_codes as Array<{ remarkCode?: string; remark?: string }>).map((rc, i) => (
                                        <Tooltip key={i} title={rc.remark ?? ''}>
                                          <Chip
                                            label={rc.remarkCode}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.5rem', height: 16, cursor: 'help' }}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
